/**
 * NOTE: This component is largely a copy of `SignUp.screen.tsx`, with the main difference being the styling
 * on the form wrappers and the removal of any redirects during authentication.
 *
 * This is used to support sign up with simple sign on, and should eventually be abstracted out in such a way that
 * it can be re-used both here and in the original sign up screen.
 */

import * as React from 'react';

import { SignUpFormModalContent } from '../signup/NewSignUpForm';
import {
  useConfirmSignupVerificationSessionCommand,
  useCreateSignupVerificationSessionCommand
} from 'legacy/auth/hooks';
import { SignUpConfirmationModalContent } from '../signup/SignUpConfirmation';
import { handleLoginNoRedirect } from 'legacy/auth/helpers';
import { LoginResponse } from 'legacy/auth/types';
import { ErrorResponse } from 'legacy/common/utils/error';

type SignUpAuthenticationFlowProps = {
  onCompleteSignUp: (loginResponse: LoginResponse) => void;
  onFailedSignUp?: (error: ErrorResponse) => void;
  onClickLogin: () => void;
};
const SignUpAuthenticationFlow = ({
  onCompleteSignUp,
  onFailedSignUp,
  onClickLogin
}: SignUpAuthenticationFlowProps) => {
  const [formValues, setFormValues] = React.useState<{
    phone: string;
    countryCode: string;
    firstName: string;
    lastName: string;
    email: string;
    country: 'US' | 'CA';
  }>();
  const [verificationSessionId, setVerificationSessionId] = React.useState<
    string | undefined
  >(undefined);

  const createSignUpVerificationSessionCommand =
    useCreateSignupVerificationSessionCommand();

  const confirmSignupVerificationSessionCommand =
    useConfirmSignupVerificationSessionCommand();

  const handleSignUp = ({
    phoneNumber,
    firstName,
    lastName,
    email,
    callingCode,
    accountCountryCode
  }) => {
    createSignUpVerificationSessionCommand.mutate(
      {
        phone: phoneNumber,
        countryCode: callingCode,
        firstName,
        lastName,
        email,
        country: accountCountryCode
      },
      {
        onSuccess: (data) => {
          setFormValues({
            phone: phoneNumber,
            firstName,
            lastName,
            email,
            countryCode: callingCode,
            country: accountCountryCode
          });
          setVerificationSessionId(data.verificationSessionId);
        }
      }
    );
  };

  const handleConfirmSignUp = async (code: string) => {
    try {
      const loginResponse =
        await confirmSignupVerificationSessionCommand.mutateAsync({
          smsCode: code,
          verificationSessionId
        });

      handleLoginNoRedirect(loginResponse);

      onCompleteSignUp(loginResponse);
    } catch (err) {
      onFailedSignUp?.(err);
    }
  };

  const handleResendCode = () => {
    createSignUpVerificationSessionCommand.mutate(formValues);
  };

  const onReset = () => {
    setFormValues(undefined);
    setVerificationSessionId(undefined);
    createSignUpVerificationSessionCommand.reset();
  };

  if (verificationSessionId) {
    return (
      <SignUpConfirmationModalContent
        onBackClick={onReset}
        onSubmit={handleConfirmSignUp}
        onResendCode={handleResendCode}
        isResendingCode={createSignUpVerificationSessionCommand.isLoading}
        isLoading={confirmSignupVerificationSessionCommand.isLoading}
        errorMessage={confirmSignupVerificationSessionCommand.error?.message}
      />
    );
  }

  return (
    <SignUpFormModalContent
      isLoading={createSignUpVerificationSessionCommand.isLoading}
      errorMessage={createSignUpVerificationSessionCommand.error?.message}
      onSubmit={handleSignUp}
      onClickLogin={onClickLogin}
    />
  );
};

export default SignUpAuthenticationFlow;
